function parseRgba(rgba) {
    // Handle null, undefined, or empty string
    if (!rgba || typeof rgba !== "string") {
        return { r: 0, g: 0, b: 0, a: 1 };
    }

    const match = rgba.match(
        /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/,
    );
    if (!match) {
        return { r: 0, g: 0, b: 0, a: 1 };
    }
    return {
        r: parseInt(match[1]),
        g: parseInt(match[2]),
        b: parseInt(match[3]),
        a: match[4] ? parseFloat(match[4]) : 1,
    };
}

// Adjust color brightness
function adjustBrightness(rgba, percent) {
    const { r, g, b, a } = parseRgba(rgba);
    const factor = 1 + percent / 100;

    const newR = Math.min(255, Math.max(0, Math.round(r * factor)));
    const newG = Math.min(255, Math.max(0, Math.round(g * factor)));
    const newB = Math.min(255, Math.max(0, Math.round(b * factor)));

    return `rgba(${newR}, ${newG}, ${newB}, ${a})`;
}

// Invert color (255 - each RGB component)
function invert(rgba) {
    const { r, g, b, a } = parseRgba(rgba);
    return `rgba(${255 - r}, ${255 - g}, ${255 - b}, ${a})`;
}

// Make color lighter by adding percentage of (255 - current value)
function makeLighter(rgba, percent) {
    const { r, g, b, a } = parseRgba(rgba);
    const newR = Math.min(255, Math.round(r + ((255 - r) * percent) / 100));
    const newG = Math.min(255, Math.round(g + ((255 - g) * percent) / 100));
    const newB = Math.min(255, Math.round(b + ((255 - b) * percent) / 100));
    return `rgba(${newR}, ${newG}, ${newB}, ${a})`;
}

// Make color darker by multiplying by factor
function makeDarker(rgba, percent) {
    const { r, g, b, a } = parseRgba(rgba);
    const factor = 1 - percent / 100;
    const newR = Math.max(0, Math.round(r * factor));
    const newG = Math.max(0, Math.round(g * factor));
    const newB = Math.max(0, Math.round(b * factor));
    return `rgba(${newR}, ${newG}, ${newB}, ${a})`;
}

// Convert hex to RGBA
function hexToRgba(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return "rgba(0, 0, 0, 1)";

    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);

    return `rgba(${r}, ${g}, ${b}, 1)`;
}

// Convert RGBA to hex
function rgbaToHex(rgba) {
    const { r, g, b } = parseRgba(rgba);
    return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

// Convert any color format to hex
function toHex(color) {
    // If it's already a hex string, return as-is
    if (typeof color === "string" && color.startsWith("#")) {
        return color;
    }

    // If it's an RGB/RGBA object, convert to hex
    if (
        typeof color === "object" &&
        color !== null &&
        color.r !== undefined &&
        color.g !== undefined &&
        color.b !== undefined
    ) {
        const r = Math.round(color.r);
        const g = Math.round(color.g);
        const b = Math.round(color.b);
        return (
            "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")
        );
    }

    // If it's a string format (rgb, rgba, hsl, hsla), convert to rgba first, then to hex
    if (typeof color === "string") {
        // Try to parse as rgba format first
        const rgba = parseRgba(color);
        if (rgba.r !== 0 || rgba.g !== 0 || rgba.b !== 0) {
            return rgbaToHex(
                `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`,
            );
        }
    }

    // Fallback: assume it's already in a format we can use
    return color;
}

// Calculate relative luminance of a color
function getLuminance(rgba) {
    const { r, g, b } = parseRgba(rgba);

    // Convert to relative luminance
    const rsRGB = r / 255;
    const gsRGB = g / 255;
    const bsRGB = b / 255;

    const rLinear =
        rsRGB <= 0.03928
            ? rsRGB / 12.92
            : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const gLinear =
        gsRGB <= 0.03928
            ? gsRGB / 12.92
            : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const bLinear =
        bsRGB <= 0.03928
            ? bsRGB / 12.92
            : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

// Get contrast color (black or white)
function getContrastColor(rgba) {
    const luminance = getLuminance(rgba);
    return luminance > 0.5 ? "rgba(0, 0, 0, 1)" : "rgba(255, 255, 255, 1)";
}

// Compare if color1 is darker than color2
function isDarkerThan(color1, color2) {
    return getLuminance(color1) < getLuminance(color2);
}

// Calculate contrast ratio between two colors
function getContrastRatio(color1, color2) {
    const lum1 = getLuminance(color1);
    const lum2 = getLuminance(color2);

    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);

    return (lighter + 0.05) / (darker + 0.05);
}

// Check if two colors have sufficient contrast
function hasGoodContrast(color1, color2, minRatio = 3.0) {
    return getContrastRatio(color1, color2) >= minRatio;
}

// Check if colors are too similar (similar hue/saturation)
function areColorsTooSimilar(color1, color2, threshold = 0.04) {
    const { r: r1, g: g1, b: b1 } = parseRgba(color1);
    const { r: r2, g: g2, b: b2 } = parseRgba(color2);

    // Calculate Euclidean distance in RGB space
    const distance = Math.sqrt(
        Math.pow(r2 - r1, 2) + Math.pow(g2 - g1, 2) + Math.pow(b2 - b1, 2),
    );

    // Normalize distance (max possible distance is sqrt(3 * 255^2))
    const normalizedDistance = distance / Math.sqrt(3 * Math.pow(255, 2));

    return normalizedDistance < threshold;
}

// Find a better color that maintains good contrast
function findBetterColor(originalColor, backgroundColor, options = {}) {
    const {
        minContrastRatio = 4.5,
        maxAttempts = 20,
        stepSize = 0.1,
    } = options;

    const { r, g, b, a } = parseRgba(originalColor);
    const bgLum = getLuminance(backgroundColor);

    // Determine if we need a lighter or darker color
    const needsLighter = bgLum < 0.5;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const factor = needsLighter
            ? 1 + attempt * stepSize // Make lighter
            : 1 - attempt * stepSize; // Make darker

        const newR = Math.min(255, Math.max(0, Math.round(r * factor)));
        const newG = Math.min(255, Math.max(0, Math.round(g * factor)));
        const newB = Math.min(255, Math.max(0, Math.round(b * factor)));

        const testColor = `rgba(${newR}, ${newG}, ${newB}, ${a})`;

        if (hasGoodContrast(testColor, backgroundColor, minContrastRatio)) {
            return testColor;
        }
    }

    // Fallback: return high contrast color
    return getContrastColor(backgroundColor);
}

/**
 * Adjust a color to achieve a target relative luminance
 * CRITICAL: Relative luminance ≠ HSL lightness!
 * - Relative luminance: Perceptual brightness (0.2126*R + 0.7152*G + 0.0722*B)
 * - HSL lightness: Geometric value ((max+min)/2)
 * We use binary search to find the HSL lightness that produces the target relative luminance
 * @param {string} color - The color to adjust in rgba format
 * @param {number} targetLuminance - The target relative luminance value (0-1)
 * @returns {object} Object containing adjusted color and correction details
 */
function adjustColorLuminance(color, targetLuminance) {
    // Validate input
    if (!color || typeof color !== "string") {
        return {
            color: "rgba(0, 0, 0, 1)",
            correctionDetails: {
                targetLuminance: targetLuminance,
                achievedLuminance: 0,
                error: targetLuminance,
                originalLuminance: 0,
            },
        };
    }

    const hsl = rgbaToHsl(color);
    const originalLuminance = getLuminance(color);

    // Clamp target luminance to valid range [0, 1]
    const clampedTarget = Math.min(1, Math.max(0, targetLuminance));

    // Binary search to find HSL lightness that gives us the target luminance
    let minL = 0;
    let maxL = 1;
    let bestL = hsl.l;
    let bestLuminance = originalLuminance;
    let bestDifference = Math.abs(originalLuminance - clampedTarget);

    const MAX_ITERATIONS = 50; // Increased for better precision
    const TOLERANCE = 0.0001; // Tighter tolerance for more accurate results

    for (let i = 0; i < MAX_ITERATIONS; i++) {
        const testL = (minL + maxL) / 2;
        const testColor = hslToRgba(hsl.h, hsl.s, testL, hsl.a);
        const testLuminance = getLuminance(testColor);
        const difference = Math.abs(testLuminance - clampedTarget);

        // Track the best result
        if (difference < bestDifference) {
            bestL = testL;
            bestLuminance = testLuminance;
            bestDifference = difference;
        }

        // Check if we're close enough
        if (difference < TOLERANCE) {
            bestL = testL;
            bestLuminance = testLuminance;
            break;
        }

        // Adjust search range
        if (testLuminance < clampedTarget) {
            minL = testL; // Need more lightness
        } else {
            maxL = testL; // Need less lightness
        }
    }

    const result = hslToRgba(hsl.h, hsl.s, bestL, hsl.a);

    return {
        color: result,
        correctionDetails: {
            targetLuminance: clampedTarget,
            achievedLuminance: bestLuminance,
            error: bestDifference,
            originalLuminance: originalLuminance,
        },
    };
}

// Validate and adjust color combinations for better readability
// Implements automatic contrast adjustment based on luminance threshold
function validateColorCombination(
    primary,
    secondary,
    fontLight,
    changedColorType = null,
) {
    const adjustments = [];
    let adjustedPrimary = primary;
    let adjustedSecondary = secondary;
    let adjustedFontLight = fontLight;

    const THRESHOLD = 0.1;

    // Get luminance values for primary (background) and fontLight (font)
    const L_BG = getLuminance(primary);
    const L_Font = getLuminance(fontLight);

    // Check if colors are too close in luminance
    const luminanceDifference = Math.abs(L_Font - L_BG);
    const isTooClose = luminanceDifference < THRESHOLD;

    // Case 1: User adjusted background color (primary)
    // If background is too close to font, adjust background AWAY from font
    if (changedColorType === "primary" && isTooClose) {
        let newL_BG;

        // Move background AWAY from font luminance to ensure at least THRESHOLD difference
        if (L_BG < L_Font) {
            // Background is darker than font → make it even DARKER
            // Target: L_Font - THRESHOLD to ensure sufficient gap
            newL_BG = L_Font - THRESHOLD;
            if (newL_BG < 0) {
                // Can't go darker enough, go lighter instead
                newL_BG = L_Font + THRESHOLD;
            }
        } else if (L_BG > L_Font) {
            // Background is lighter than font → make it even LIGHTER
            // Target: L_Font + THRESHOLD to ensure sufficient gap
            newL_BG = L_Font + THRESHOLD;
            if (newL_BG > 1) {
                // Can't go lighter enough, go darker instead
                newL_BG = L_Font - THRESHOLD;
            }
        } else {
            // Equal luminance - decide based on which direction has more room
            if (L_BG <= 0.5) {
                // Closer to black, make background darker
                newL_BG = Math.max(0, L_BG - THRESHOLD);
            } else {
                // Closer to white, make background lighter
                newL_BG = Math.min(1, L_BG + THRESHOLD);
            }
        }

        // Clamp to valid range
        newL_BG = Math.min(1, Math.max(0, newL_BG));

        const correctionResult = adjustColorLuminance(primary, newL_BG);
        adjustedPrimary = correctionResult.color;

        // Verify result
        const finalL_BG = getLuminance(adjustedPrimary);
        let finalDifference = Math.abs(L_Font - finalL_BG);

        // SAFETY: If adjustment still doesn't meet threshold, adjust font instead
        if (finalDifference < THRESHOLD) {
            let newL_Font;

            if (finalL_BG < L_Font) {
                // Background became darker, make font lighter
                newL_Font = finalL_BG + THRESHOLD;
                if (newL_Font > 1) {
                    newL_Font = Math.max(0, finalL_BG - THRESHOLD);
                }
            } else {
                // Background became lighter, make font darker
                newL_Font = finalL_BG - THRESHOLD;
                if (newL_Font < 0) {
                    newL_Font = Math.min(1, finalL_BG + THRESHOLD);
                }
            }

            newL_Font = Math.min(1, Math.max(0, newL_Font));

            const fontCorrectionResult = adjustColorLuminance(
                fontLight,
                newL_Font,
            );
            adjustedFontLight = fontCorrectionResult.color;

            // Recalculate final difference
            const finalL_Font = getLuminance(adjustedFontLight);
            finalDifference = Math.abs(finalL_BG - finalL_Font);

            // Add font adjustment to adjustments array
            adjustments.push({
                original: fontLight,
                adjusted: adjustedFontLight,
                reason: "The font color was also adjusted because the background adjustment alone was insufficient to meet the contrast threshold.",
                colorType: "fontLight",
                wasAutoAdjusted: true,
                correctionDetails: {
                    ...fontCorrectionResult.correctionDetails,
                    finalLuminance: finalL_Font,
                    finalDifference: finalDifference,
                    threshold: THRESHOLD,
                    meetsThreshold: finalDifference >= THRESHOLD,
                },
            });
        }

        adjustments.push({
            original: primary,
            adjusted: adjustedPrimary,
            reason: "The background color has been automatically adjusted to ensure proper contrast against the font color for better readability.",
            colorType: "primary",
            wasAutoAdjusted: true,
            correctionDetails: {
                ...correctionResult.correctionDetails,
                finalLuminance: finalL_BG,
                finalDifference: finalDifference,
                threshold: THRESHOLD,
                meetsThreshold: finalDifference >= THRESHOLD,
            },
        });
    }

    // Case 2: User adjusted font color (fontLight)
    // If font is too close to background, adjust font AWAY from background
    if (changedColorType === "fontLight" && isTooClose) {
        let newL_Font;

        // Move font AWAY from background luminance to ensure at least THRESHOLD difference
        if (L_Font < L_BG) {
            // Font is darker than background → make it even DARKER
            // Target: L_BG - THRESHOLD to ensure sufficient gap
            newL_Font = L_BG - THRESHOLD;
            if (newL_Font < 0) {
                // Can't go darker enough, go lighter instead
                newL_Font = L_BG + THRESHOLD;
            }
        } else if (L_Font > L_BG) {
            // Font is lighter than background → make it even LIGHTER
            // Target: L_BG + THRESHOLD to ensure sufficient gap
            newL_Font = L_BG + THRESHOLD;
            if (newL_Font > 1) {
                // Can't go lighter enough, go darker instead
                newL_Font = L_BG - THRESHOLD;
            }
        } else {
            // Equal luminance - decide based on which direction has more room
            if (L_Font <= 0.5) {
                // Closer to black, make font darker
                newL_Font = Math.max(0, L_Font - THRESHOLD);
            } else {
                // Closer to white, make font lighter
                newL_Font = Math.min(1, L_Font + THRESHOLD);
            }
        }

        // Clamp to valid range
        newL_Font = Math.min(1, Math.max(0, newL_Font));

        const correctionResult = adjustColorLuminance(fontLight, newL_Font);
        adjustedFontLight = correctionResult.color;

        // Verify result
        const finalL_Font = getLuminance(adjustedFontLight);
        let finalDifference = Math.abs(L_BG - finalL_Font);

        // SAFETY: If adjustment still doesn't meet threshold, adjust background instead
        if (finalDifference < THRESHOLD) {
            let newL_BG;

            if (finalL_Font < L_BG) {
                // Font became darker, make background lighter
                newL_BG = finalL_Font + THRESHOLD;
                if (newL_BG > 1) {
                    newL_BG = Math.max(0, finalL_Font - THRESHOLD);
                }
            } else {
                // Font became lighter, make background darker
                newL_BG = finalL_Font - THRESHOLD;
                if (newL_BG < 0) {
                    newL_BG = Math.min(1, finalL_Font + THRESHOLD);
                }
            }

            newL_BG = Math.min(1, Math.max(0, newL_BG));

            const bgCorrectionResult = adjustColorLuminance(primary, newL_BG);
            adjustedPrimary = bgCorrectionResult.color;

            // Recalculate final difference
            const finalL_BG = getLuminance(adjustedPrimary);
            finalDifference = Math.abs(finalL_Font - finalL_BG);

            // Add background adjustment to adjustments array
            adjustments.push({
                original: primary,
                adjusted: adjustedPrimary,
                reason: "The background color was also adjusted because the font adjustment alone was insufficient to meet the contrast threshold.",
                colorType: "primary",
                wasAutoAdjusted: true,
                correctionDetails: {
                    ...bgCorrectionResult.correctionDetails,
                    finalLuminance: finalL_BG,
                    finalDifference: finalDifference,
                    threshold: THRESHOLD,
                    meetsThreshold: finalDifference >= THRESHOLD,
                },
            });
        }

        adjustments.push({
            original: fontLight,
            adjusted: adjustedFontLight,
            reason: "The font color has been automatically adjusted to ensure proper contrast against the background color for better readability.",
            colorType: "fontLight",
            wasAutoAdjusted: true,
            correctionDetails: {
                ...correctionResult.correctionDetails,
                finalLuminance: finalL_Font,
                finalDifference: finalDifference,
                threshold: THRESHOLD,
                meetsThreshold: finalDifference >= THRESHOLD,
            },
        });
    }

    return {
        primary: adjustedPrimary,
        secondary: adjustedSecondary,
        fontLight: adjustedFontLight,
        adjustments,
    };
}

// Convert RGBA to HSL
function rgbaToHsl(rgba) {
    const { r, g, b, a } = parseRgba(rgba);

    // Convert RGB to 0-1 range
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const delta = max - min;

    // Calculate Lightness
    const l = (max + min) / 2;

    // Calculate Saturation
    let s = 0;
    if (delta !== 0) {
        s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    }

    // Calculate Hue
    let h = 0;
    if (delta !== 0) {
        if (max === rNorm) {
            h = ((gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0)) / 6;
        } else if (max === gNorm) {
            h = ((bNorm - rNorm) / delta + 2) / 6;
        } else {
            h = ((rNorm - gNorm) / delta + 4) / 6;
        }
    }

    return { h, s, l, a };
}

// Convert HSL to RGBA
function hslToRgba(h, s, l, a = 1) {
    let r, g, b;

    if (s === 0) {
        // Achromatic (gray)
        r = g = b = l;
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`;
}

// Check if a color is grayscale (all RGB components within threshold of each other)
function isGrayscale(rgba, threshold = 10) {
    const { r, g, b } = parseRgba(rgba);
    return (
        Math.abs(r - g) < threshold &&
        Math.abs(r - b) < threshold &&
        Math.abs(g - b) < threshold
    );
}

// Calculate dimmed font color by blending towards section background lightness
function calculateDimmedFont(
    fontColor,
    sectionBgColor,
    dimFactor = null,
    fontWeight = null,
    bgWeight = null,
) {
    // Auto-adjust parameters based on whether font is grayscale
    let actualDimFactor, actualFontWeight, actualBgWeight;

    if (
        dimFactor === null ||
        fontWeight === null ||
        bgWeight === null ||
        dimFactor === undefined ||
        fontWeight === undefined ||
        bgWeight === undefined
    ) {
        // Auto-calculate based on font color type and section background
        if (isGrayscale(fontColor, 10)) {
            // Grayscale font
            actualDimFactor = 0.7;
            actualFontWeight = 1;
            actualBgWeight = 1;
        } else {
            // Colored font: check if section background is closer to black or white
            const sectionLuminance = getLuminance(sectionBgColor);

            if (sectionLuminance < 0.5) {
                // Section background is closer to black
                actualDimFactor = 0.7;
                actualFontWeight = 0.9;
                actualBgWeight = 0.6;
            } else {
                // Section background is closer to white
                actualDimFactor = 0.9;
                actualFontWeight = 0.6;
                actualBgWeight = 0.9;
            }
        }
    } else {
        // Use provided values
        actualDimFactor = dimFactor;
        actualFontWeight = fontWeight;
        actualBgWeight = bgWeight;
    }

    // Convert colors to HSL
    const fontHsl = rgbaToHsl(fontColor);
    const sectionHsl = rgbaToHsl(sectionBgColor);

    // Calculate blended lightness with weights
    // L_dimFont = (L_font × (1 - dim_factor_font) × font_weight) + (L_secbg × dim_factor_font × bg_weight)
    const lDim =
        fontHsl.l * (1 - actualDimFactor) * actualFontWeight +
        sectionHsl.l * actualDimFactor * actualBgWeight;

    // Keep original hue and saturation, blend lightness
    return hslToRgba(fontHsl.h, fontHsl.s, lDim, fontHsl.a);
}

// Calculate dimmed icon color by blending towards section background lightness
// L_dimIcon = (L_font × (1 - dim_factor_icon)) + (L_secbg × dim_factor_icon)
function calculateDimmedIcon(fontColor, sectionBgColor, dimFactorIcon = null) {
    // Auto-adjust dim factor based on font color type and section background
    let actualDimFactorIcon;

    if (dimFactorIcon === null || dimFactorIcon === undefined) {
        // Auto-calculate based on font color type and section background
        if (isGrayscale(fontColor, 10)) {
            // Grayscale font
            actualDimFactorIcon = 0.55;
        } else {
            // Colored font: check if section background is closer to black or white
            const sectionLuminance = getLuminance(sectionBgColor);

            if (sectionLuminance < 0.5) {
                // Section background is closer to black
                actualDimFactorIcon = 0.9;
            } else {
                // Section background is closer to white
                actualDimFactorIcon = 0.6;
            }
        }
    } else {
        // Use provided value
        actualDimFactorIcon = dimFactorIcon;
    }

    // Convert colors to HSL
    const fontHsl = rgbaToHsl(fontColor);
    const sectionHsl = rgbaToHsl(sectionBgColor);

    // Calculate blended lightness for icons
    // L_dimIcon = (L_font × (1 - dim_factor_icon)) + (L_secbg × dim_factor_icon)
    const lDimIcon =
        fontHsl.l * (1 - actualDimFactorIcon) +
        sectionHsl.l * actualDimFactorIcon;

    // Keep original hue and saturation, blend lightness
    return hslToRgba(fontHsl.h, fontHsl.s, lDimIcon, fontHsl.a);
}

// Calculate disabled element background color based on section background
// H_disabledbg = H_secbg (keep same hue)
// S_disabledbg = S_secbg (keep same saturation)
// L_disabledbg based on HSL Lightness ranges (customizable thresholds and adjustments)
// Default behavior: Darken for light backgrounds, lighten for very dark backgrounds
function calculateDisabledBg(sectionBgColor, params = null) {
    // Default parameters - darken light backgrounds, lighten very dark backgrounds
    const defaultParams = {
        threshold1: 0.7, // Very light backgrounds (L > 0.7)
        adjustment1: -0.03, // Darken by -0.03
        threshold2: 0.5, // Light backgrounds (L > 0.5)
        adjustment2: -0.033, // Darken by -0.033
        threshold3: 0.2, // Dark backgrounds (L > 0.2)
        adjustment3: -0.037, // Darken by -0.037
        adjustmentFinal: 0.1, // Very dark backgrounds: Lighten by +0.1
    };

    const {
        threshold1,
        adjustment1,
        threshold2,
        adjustment2,
        threshold3,
        adjustment3,
        adjustmentFinal,
    } = params || defaultParams;

    // Convert section background to HSL
    const sectionHsl = rgbaToHsl(sectionBgColor);

    // Get HSL Lightness
    const sectionLightness = sectionHsl.l;

    let lDisabledBg;

    if (sectionLightness > threshold1) {
        // Very light background
        lDisabledBg = sectionLightness + adjustment1;
    } else if (sectionLightness > threshold2) {
        // Light background
        lDisabledBg = sectionLightness + adjustment2;
    } else if (sectionLightness > threshold3) {
        // Dark background
        lDisabledBg = sectionLightness + adjustment3;
    } else {
        // Very dark background
        lDisabledBg = sectionLightness + adjustmentFinal;
    }

    // Clamp lightness to valid range [0, 1]
    lDisabledBg = Math.min(1, Math.max(0, lDisabledBg));

    // Keep original hue and saturation, adjust only lightness
    return hslToRgba(sectionHsl.h, sectionHsl.s, lDisabledBg, sectionHsl.a);
}

// Calculate agent text color based on section background and font color
// If Section_BG_L >= 0.95 and Font_L <= 0.05:
//   Agent_Text_H = 220, Agent_Text_S = 0.85, Agent_Text_L = 0.3
// Else:
//   Agent_Text_H = (Font_H + 120) % 360
//   Agent_Text_S = Max(Font_S, 0.7)
//   If Section_BG_L > 0.5: Agent_Text_L = 0.15
//   Else: Agent_Text_L = 0.9
function calculateAgentText(fontColor, sectionBgColor) {
    const fontHsl = rgbaToHsl(fontColor); // { h, s, l, a } with h in [0,1]
    const sectionHsl = rgbaToHsl(sectionBgColor);

    let agentH, agentS, agentL;

    // Convert HSL hue to degrees for easier math
    const fontHueDeg = fontHsl.h * 360;

    // Condition: If Section_BG_L >= 0.95 and Font_L <= 0.05
    if (sectionHsl.l >= 0.95 && fontHsl.l <= 0.05) {
        agentH = 220; // degrees
        agentS = 0.85;
        agentL = 0.3;
    } else {
        // Else
        agentH = (fontHueDeg + 120) % 360; // (Font_H + 120) % 360
        agentS = Math.max(fontHsl.s, 0.7); // Max(Font_S, 0.7)

        // Agent_Text_L based on Section_BG_L
        if (sectionHsl.l > 0.5) {
            agentL = 0.15;
        } else {
            agentL = 0.9;
        }
    }

    // Convert back to 0–1 range for hslToRgba
    return hslToRgba(agentH / 360, agentS, agentL, fontHsl.a);
}

// Calculate disabled button background color based on button background
// H_disabledButtonBg = H_buttonBg (keep same hue)
// S_disabledButtonBg = S_buttonBg (keep same saturation)
// L_disabledButtonBg based on HSL Lightness ranges (customizable thresholds and adjustments)
// Default behavior: Lighten buttons when disabled (all adjustments are positive)
function calculateDisabledButtonBg(buttonBgColor, params = null) {
    // Default parameters - all adjustments lighten the button
    const defaultParams = {
        threshold1: 0.7, // Very light buttons (L > 0.7)
        adjustment1: 0.18, // Lighten by +0.18
        threshold2: 0.5, // Light buttons (L > 0.5)
        adjustment2: 0.15, // Lighten by +0.15
        threshold3: 0.2, // Dark buttons (L > 0.2)
        adjustment3: 0.035, // Lighten by +0.035
        adjustmentFinal: 0.04, // Very dark buttons: Lighten by +0.04
    };

    const {
        threshold1,
        adjustment1,
        threshold2,
        adjustment2,
        threshold3,
        adjustment3,
        adjustmentFinal,
    } = params || defaultParams;

    // Convert button background to HSL
    const buttonHsl = rgbaToHsl(buttonBgColor);

    // Get HSL Lightness
    const buttonLightness = buttonHsl.l;

    let lDisabledButtonBg;

    if (buttonLightness > threshold1) {
        // Very light button background
        lDisabledButtonBg = buttonLightness + adjustment1;
    } else if (buttonLightness > threshold2) {
        // Light button background
        lDisabledButtonBg = buttonLightness + adjustment2;
    } else if (buttonLightness > threshold3) {
        // Dark button background
        lDisabledButtonBg = buttonLightness + adjustment3;
    } else {
        // Very dark button background
        lDisabledButtonBg = buttonLightness + adjustmentFinal;
    }

    // Clamp lightness to valid range [0, 1]
    lDisabledButtonBg = Math.min(1, Math.max(0, lDisabledButtonBg));

    // Keep original hue and saturation, adjust only lightness
    return hslToRgba(buttonHsl.h, buttonHsl.s, lDisabledButtonBg, buttonHsl.a);
}

// Calculate link color based on section background
// Ensures good contrast and visibility across different color themes
function calculateFontLink(sectionBgColor) {
    const sectionHsl = rgbaToHsl(sectionBgColor);
    const sectionLuminance = getLuminance(sectionBgColor);

    // Convert hue to 0–360 degrees
    const sectionHueDeg = sectionHsl.h * 360;
    const defaultHue = 220; // Blue hue
    const defaultS = 1; // Full saturation
    let linkL;

    // Determine lightness based on background luminance for good contrast
    if (sectionLuminance < 0.2) {
        // Very dark background - use bright link color
        linkL = 0.7;
    } else if (sectionLuminance < 0.4) {
        // Dark background - use lighter link color
        linkL = 0.6;
    } else if (sectionLuminance < 0.7) {
        // Medium background - use medium link color
        linkL = 0.45;
    } else {
        // Light background - use darker link color
        linkL = 0.35;
    }

    // If section background is too close to blue hue, adjust lightness further for distinction
    if (Math.abs(sectionHueDeg - defaultHue) < 40) {
        if (sectionLuminance < 0.5) {
            linkL = Math.min(0.8, linkL + 0.15);
        } else {
            linkL = Math.max(0.25, linkL - 0.15);
        }
    }

    // Convert back to normalized hue [0–1]
    const linkH = defaultHue / 360;

    return hslToRgba(linkH, defaultS, linkL, 1);
}

/**
 * Calculate hover green color based on highlight background (secondary) luminance
 * Uses constant green (0, 128, 0) as base
 * Hover_Constant_Green.H = Constant_Green.H
 * Hover_Constant_Green.S = Constant_Green.S
 * if Highlight_BG.L < 0.65: Hover_Constant_Green.L = Constant_Green.L+0.1
 * Else: Hover_Constant_Green.L = Constant_Green.L-0.2
 * @param {string} highlightBg - The highlight/secondary background color in rgba format
 * @param {string} constantGreen - The constant green color in rgba format (default: rgb(0, 128, 0, 1))
 * @returns {string} The calculated hover green color in rgba format
 */
function calculateFontHoverGreen(highlightBg, constantGreen) {
    const greenHsl = rgbaToHsl(constantGreen);
    const highlightHsl = rgbaToHsl(highlightBg);

    let hoverGreenL;
    if (highlightHsl.l < 0.65) {
        hoverGreenL = greenHsl.l + 0.1;
    } else {
        hoverGreenL = greenHsl.l - 0.2;
    }

    // Clamp lightness to valid range [0, 1]
    hoverGreenL = Math.min(1, Math.max(0, hoverGreenL));

    return hslToRgba(greenHsl.h, greenHsl.s, hoverGreenL, greenHsl.a);
}

/**
 * Calculate hover grey color based on highlight background (secondary) luminance
 * Uses constant grey (128, 128, 128) as base
 * Hover_Constant_Grey.H = Constant_Grey.H
 * Hover_Constant_Grey.S = Constant_Grey.S
 * if Highlight_BG.L < 0.7: Hover_Constant_Grey.L = Constant_Grey.L+0.1
 * Else: Hover_Constant_Grey.L = Constant_Grey.L-0.25
 * @param {string} highlightBg - The highlight/secondary background color in rgba format
 * @param {string} constantGrey - The constant grey color in rgba format (default: rgba(128, 128, 128, 1))
 * @returns {string} The calculated hover grey color in rgba format
 */
function calculateFontHoverGrey(highlightBg, constantGrey) {
    const greyHsl = rgbaToHsl(constantGrey);
    const highlightHsl = rgbaToHsl(highlightBg);

    let hoverGreyL;
    if (highlightHsl.l < 0.7) {
        hoverGreyL = greyHsl.l + 0.1;
    } else {
        hoverGreyL = greyHsl.l - 0.25;
    }

    // Clamp lightness to valid range [0, 1]
    hoverGreyL = Math.min(1, Math.max(0, hoverGreyL));

    return hslToRgba(greyHsl.h, greyHsl.s, hoverGreyL, greyHsl.a);
}

/**
 * Calculate constant light grey color based on primary font luminance
 * Default = rgba(189, 189, 189, 1)
 * If PrimaryFont_L > 0.4, decrease RGB by 50
 * @param {string} fontLight - The primary font color in rgba format
 * @returns {string} The calculated constant light grey color in rgba format
 */
function calculateConstantLightGrey(fontLight) {
    const defaultR = 189;
    const defaultG = 189;
    const defaultB = 189;

    const luminance = getLuminance(fontLight);

    // If luminance > 0.4, decrease RGB values by 50
    if (luminance > 0.4) {
        const newR = Math.max(0, defaultR - 50);
        const newG = Math.max(0, defaultG - 50);
        const newB = Math.max(0, defaultB - 50);
        return `rgba(${newR}, ${newG}, ${newB}, 1)`;
    }

    // Otherwise, return the default
    return `rgba(${defaultR}, ${defaultG}, ${defaultB}, 1)`;
}

/**
 * Calculate hovered constant light grey color based on constant light grey and section background
 * Hover_Constant_LightGrey.H = Constant_LightGrey.H
 * Hover_Constant_LightGrey.S = Constant_LightGrey.S
 * if Highlight_BG.L < 0.5: Hover_Constant_LightGrey.L = Constant_LightGrey.L+0.1
 * Else: Hover_Constant_LightGrey.L = Constant_LightGrey.L-0.25
 * @param {string} sectionBg - The section/highlight background color in rgba format
 * @param {string} constantLightGrey - The constant light grey color in rgba format
 * @returns {string} The calculated hovered constant light grey color in rgba format
 */
function calculateFontHoverLightGrey(sectionBg, constantLightGrey) {
    const lightGreyHsl = rgbaToHsl(constantLightGrey);
    const sectionHsl = rgbaToHsl(sectionBg);

    let hoverLightGreyL;
    if (sectionHsl.l < 0.5) {
        hoverLightGreyL = lightGreyHsl.l + 0.1;
    } else {
        hoverLightGreyL = lightGreyHsl.l - 0.25;
    }

    // Clamp lightness to valid range [0, 1]
    hoverLightGreyL = Math.min(1, Math.max(0, hoverLightGreyL));

    return hslToRgba(
        lightGreyHsl.h,
        lightGreyHsl.s,
        hoverLightGreyL,
        lightGreyHsl.a,
    );
}

/**
 * Calculate inactive opacity for associated non-members based on section background luminance
 * @param {string} sectionBg - The section background color in rgba format
 * @returns {number} The calculated opacity value (0.22D for dark themes, 0.25L for light themes)
 */
function calculateInactiveOpacity(sectionBg) {
    const luminance = getLuminance(sectionBg);

    // If section background luminance < 0.4 (Dark theme), use 0.22
    // Otherwise (Light theme), use 0.25
    return luminance < 0.4 ? 0.22 : 0.25;
}

/**
 * Calculate disassociated non-member opacity based on section background luminance
 * @param {string} sectionBg - The section background color in rgba format
 * @returns {number} The calculated opacity value (0.17D for dark themes, 0.15L for light themes)
 */
function calculateDisassociatedMemberOpacity(sectionBg) {
    const luminance = getLuminance(sectionBg);

    // If section background luminance < 0.4 (Dark theme), use 0.17
    // Otherwise (Light theme), use 0.15
    return luminance < 0.4 ? 0.17 : 0.15;
}

/**
 * Calculate dynamic pauseDark color based on primary background luminance
 * @param {string} primaryBg - The primary background color in rgba format
 * @returns {string} The calculated pauseDark color in rgba format
 */
function calculatePauseDark(primaryBg) {
    const defaultR = 54;
    const defaultG = 69;
    const defaultB = 79;

    const luminance = getLuminance(primaryBg);

    // If luminance < 0.4, increase RGB values
    if (luminance < 0.4) {
        const newR = Math.min(255, defaultR + 24);
        const newG = Math.min(255, defaultG + 36);
        const newB = Math.min(255, defaultB + 36);
        return `rgba(${newR}, ${newG}, ${newB}, 1)`;
    }

    // Otherwise, return the default
    return `rgba(${defaultR}, ${defaultG}, ${defaultB}, 1)`;
}

/**
 * Calculate dropdown background color based on accent background luminance
 * Used for both enabled and disabled dropdowns (shift pause timers, membership privileges, etc)
 * Font color differs: enabled uses fontLight, disabled uses icon
 * Formula:
 * - If Accent_BG_L > 0.7, then Dropdown_BG_L = Accent_BG_L + 0.18
 * - Else If Accent_BG_L > 0.5, then Dropdown_BG_L = Accent_BG_L + 0.15
 * - Else If Accent_BG_L > 0.2, then Dropdown_BG_L = Accent_BG_L + 0.035
 * - Else Dropdown_BG_L = Accent_BG_L + 0.04
 * @param {string} accentBg - The accent background color in rgba format
 * @returns {string} The calculated dropdown background color in rgba format
 */
function calculateDropdownBg(accentBg) {
    const accentHsl = rgbaToHsl(accentBg);
    const accentL = accentHsl.l;

    let dropdownL;

    if (accentL > 0.7) {
        dropdownL = accentL + 0.18;
    } else if (accentL > 0.5) {
        dropdownL = accentL + 0.15;
    } else if (accentL > 0.2) {
        dropdownL = accentL + 0.035;
    } else {
        dropdownL = accentL + 0.04;
    }

    // Clamp lightness to valid range [0, 1]
    dropdownL = Math.min(1, Math.max(0, dropdownL));

    // Keep original hue and saturation, adjust only lightness
    return hslToRgba(accentHsl.h, accentHsl.s, dropdownL, accentHsl.a);
}

/**
 * Calculate Implicit Spinner background color based on accent background luminance
 * Used when chat images are loading in conversation panel
 * Background uses the same formula as dropdown:
 * - If Accent_BG_L > 0.7, then Dropdown_BG_L = Accent_BG_L + 0.18
 * - Else If Accent_BG_L > 0.5, then Dropdown_BG_L = Accent_BG_L + 0.15
 * - Else If Accent_BG_L > 0.2, then Dropdown_BG_L = Accent_BG_L + 0.035
 * - Else Dropdown_BG_L = Accent_BG_L + 0.04
 * Spinner color: Primary Font color (fontPrimary from theme)
 * @param {string} accentBg - The accent background color in rgba format
 * @returns {string} The calculated implicit spinner background color in rgba format
 */
function calculateImplicitSpinnerBg(accentBg) {
    // Use the same background calculation as dropdown
    return calculateDropdownBg(accentBg);
}

/**
 * Pinned activity drawer borders (list dividers, header rule, header pill).
 * Drawer chrome uses the primary background; shift bar pills use sectionBorder on accent.
 *
 * - If Primary_BG_L > 0.7, use primary border (visible edge on light drawer surfaces)
 * - Else if Accent_BG_L > 0.7, use section border
 * - Else use primary border
 *
 * @param {string} accentBg
 * @param {string} primaryBorder
 * @param {string} sectionBorder
 * @param {string} primaryBg
 * @returns {string}
 */
function calculateActivityDrawerListBorder(
    accentBg,
    primaryBorder,
    sectionBorder,
    primaryBg,
) {
    const primaryL = rgbaToHsl(primaryBg).l;

    if (primaryL > 0.7) {
        return primaryBorder;
    }

    const accentL = rgbaToHsl(accentBg).l;

    return accentL > 0.7 ? sectionBorder : primaryBorder;
}

export {
    adjustBrightness,
    getContrastColor,
    invert,
    getLuminance,
    isDarkerThan,
    makeLighter,
    makeDarker,
    hexToRgba,
    rgbaToHex,
    parseRgba,
    toHex,
    getContrastRatio,
    hasGoodContrast,
    areColorsTooSimilar,
    findBetterColor,
    validateColorCombination,
    adjustColorLuminance,
    rgbaToHsl,
    hslToRgba,
    calculateDimmedFont,
    calculateDimmedIcon,
    calculateDisabledBg,
    calculateAgentText,
    calculateDisabledButtonBg,
    isGrayscale,
    calculateFontLink,
    calculateFontHoverGreen,
    calculateFontHoverGrey,
    calculateConstantLightGrey,
    calculateFontHoverLightGrey,
    calculateInactiveOpacity,
    calculateDisassociatedMemberOpacity,
    calculatePauseDark,
    calculateDropdownBg,
    calculateImplicitSpinnerBg,
    calculateActivityDrawerListBorder,
};
